import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, LinearProgress,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, Tab, Tabs, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Speed as SpeedIcon, CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon, TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import api from '../services/api';

const pct = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
const num = (v) => v == null ? '—' : Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 0 });

const oeeColor = (v) => {
  if (v == null) return '#9e9e9e';
  if (v >= 0.85) return '#2e7d32';
  if (v >= 0.65) return '#f57c00';
  return '#c62828';
};

function MetricCard({ label, value, sub, color, icon }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: 38 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// ──────────────────── Dashboard tab ────────────────────
function DashboardTab({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await api.get(`/oee/dashboard/?${p}`);
      setData(r.data);
    } catch { setError('Помилка завантаження даних'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!s && !loading && <Alert severity="info">За вказаний період даних немає.</Alert>}
      {s && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <MetricCard label="OEE загальний" value={pct(s.oee)}
                sub={`${s.shift_count} змін`}
                icon={<TrendingUpIcon fontSize="inherit" />} color={oeeColor(s.oee)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard label="Доступність" value={pct(s.availability)}
                sub={`Втрати: ${(s.total_loss_stop_min || 0).toFixed(0)} хв`}
                icon={<AccessTimeIcon fontSize="inherit" />} color="#1565c0" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard label="Продуктивність" value={pct(s.performance)}
                sub={`Вироблено: ${num(s.total_produced)} шт`}
                icon={<SpeedIcon fontSize="inherit" />} color="#e65100" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard label="Якість" value={pct(s.quality)}
                sub={`Брак: ${num(s.total_defect)} шт`}
                icon={<CheckCircleIcon fontSize="inherit" />} color="#2e7d32" />
            </Grid>
          </Grid>

          {/* Waterfall: час */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Баланс часу (хв)</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  { label: 'Зміна', val: s.total_shift_min, color: '#1565c0' },
                  { label: 'Планові зупинки', val: s.total_planned_stop_min, color: '#e65100' },
                  { label: 'Плановий час', val: s.total_planned_min, color: '#1976d2' },
                  { label: 'Втрати', val: s.total_loss_stop_min, color: '#c62828' },
                  { label: 'Час роботи', val: s.total_run_min, color: '#2e7d32' },
                ].map(({ label, val, color }) => (
                  <Box key={label} sx={{ textAlign: 'center', minWidth: 100 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color }}>{(val || 0).toFixed(0)}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* По машинах */}
          {(data?.by_machine || []).length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>OEE по ТПА</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                      <TableCell>ТПА</TableCell>
                      <TableCell align="center">Змін</TableCell>
                      <TableCell align="center">Вироблено</TableCell>
                      <TableCell align="center">Брак</TableCell>
                      <TableCell align="center">План. хв</TableCell>
                      <TableCell align="center">Роб. хв</TableCell>
                      <TableCell align="center">Доступність</TableCell>
                      <TableCell align="center">Продуктивність</TableCell>
                      <TableCell align="center">Якість</TableCell>
                      <TableCell align="center">OEE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.by_machine || []).map((m) => (
                      <TableRow key={m.machine_id} hover>
                        <TableCell><strong>{m.machine_name}</strong></TableCell>
                        <TableCell align="center">{m.shift_count}</TableCell>
                        <TableCell align="center">{num(m.produced)}</TableCell>
                        <TableCell align="center">{num(m.defect)}</TableCell>
                        <TableCell align="center">{(m.planned_min || 0).toFixed(0)}</TableCell>
                        <TableCell align="center">{(m.run_min || 0).toFixed(0)}</TableCell>
                        <TableCell align="center">{pct(m.availability)}</TableCell>
                        <TableCell align="center">{pct(m.performance)}</TableCell>
                        <TableCell align="center">{pct(m.quality)}</TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={700} sx={{ color: oeeColor(m.oee) }}>{pct(m.oee)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Топ простоїв */}
            {(data?.top_downtime_reasons || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Топ причини простоїв</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                        <TableCell>Код</TableCell>
                        <TableCell>Причина</TableCell>
                        <TableCell align="center">Тип</TableCell>
                        <TableCell align="center">Хвилин</TableCell>
                        <TableCell align="center">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_downtime_reasons.map((r) => (
                        <TableRow key={r.code} hover>
                          <TableCell><Chip label={r.code} size="small" /></TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell align="center">
                            <Chip label={r.type === 'planned' ? 'Плановий' : 'Втрата'}
                              color={r.type === 'planned' ? 'default' : 'error'} size="small" />
                          </TableCell>
                          <TableCell align="center">{r.minutes}</TableCell>
                          <TableCell align="center">{r.pct}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
            {/* Топ браку */}
            {(data?.top_defect_reasons || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Топ причини браку</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                        <TableCell>Код</TableCell>
                        <TableCell>Причина</TableCell>
                        <TableCell align="center">Шт</TableCell>
                        <TableCell align="center">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_defect_reasons.map((r) => (
                        <TableRow key={r.code} hover>
                          <TableCell><Chip label={r.code} size="small" color="warning" /></TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell align="center">{r.qty}</TableCell>
                          <TableCell align="center">{r.pct}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>

          {data?.plan_fulfillment_pct != null && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2">
                  Виконання плану: <strong style={{ color: oeeColor(data.plan_fulfillment_pct / 100) }}>
                    {data.plan_fulfillment_pct}%
                  </strong>
                  {data.plan_qty > 0 && <span> (план {num(data.plan_qty)} шт)</span>}
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

// ──────────────────── Reports tab ────────────────────
function ReportsTab({ dateFrom, dateTo }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [norms, setNorms] = useState([]);
  const [workShifts, setWorkShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), work_shift: '', machine: '', product_norm: '',
    operator_name: '', shift_duration_min: 720, total_produced: 0, actual_cycle_sec: '', comment: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await api.get(`/oee/reports/?${p}`);
      setReports(r.data?.results || r.data || []);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/machines/?page_size=200').then(r => setMachines(r.data?.results || r.data || []));
    api.get('/oee/product-norms/?page_size=500').then(r => setNorms(r.data?.results || r.data || []));
    api.get('/work-shifts/?is_active=true').then(r => setWorkShifts(r.data?.results || r.data || []));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      date: new Date().toISOString().slice(0, 10), work_shift: '', machine: '', product_norm: '',
      operator_name: '', shift_duration_min: 720, total_produced: 0, actual_cycle_sec: '', comment: '',
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      date: row.date, work_shift: row.work_shift || '', machine: row.machine, product_norm: row.product_norm,
      operator_name: row.operator_name, shift_duration_min: row.shift_duration_min,
      total_produced: row.total_produced, actual_cycle_sec: row.actual_cycle_sec || '', comment: row.comment,
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, actual_cycle_sec: form.actual_cycle_sec || null };
    if (editing) await api.patch(`/oee/reports/${editing}/`, payload);
    else await api.post('/oee/reports/', payload);
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm('Видалити звіт?')) return;
    await api.delete(`/oee/reports/${id}/`); load();
  };

  const expand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    setExpandedId(id);
    const r = await api.get(`/oee/reports/${id}/`);
    setExpandedData(r.data);
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Новий звіт</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Дата</TableCell>
              <TableCell>Зміна</TableCell>
              <TableCell>ТПА</TableCell>
              <TableCell>Виріб</TableCell>
              <TableCell>Оператор</TableCell>
              <TableCell align="center">Вироблено</TableCell>
              <TableCell align="center">Придатних</TableCell>
              <TableCell align="center">Брак</TableCell>
              <TableCell align="center">Дост.</TableCell>
              <TableCell align="center">Прод.</TableCell>
              <TableCell align="center">Якість</TableCell>
              <TableCell align="center">OEE</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((r) => [
              <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => expand(r.id)}>
                <TableCell>{r.date}</TableCell>
                <TableCell><Chip label={r.work_shift_name || r.shift_display || '—'} size="small" /></TableCell>
                <TableCell>{r.machine_name}</TableCell>
                <TableCell>{r.product_name}</TableCell>
                <TableCell>{r.operator_name || '—'}</TableCell>
                <TableCell align="center">{num(r.total_produced)}</TableCell>
                <TableCell align="center">{num(r.good_qty)}</TableCell>
                <TableCell align="center">{num(r.defect_qty)}</TableCell>
                <TableCell align="center">{pct(r.availability)}</TableCell>
                <TableCell align="center">{pct(r.performance)}</TableCell>
                <TableCell align="center">{pct(r.quality)}</TableCell>
                <TableCell align="center">
                  <Typography fontWeight={700} sx={{ color: oeeColor(r.oee) }}>{pct(r.oee)}</Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); del(r.id); }}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>,
              expandedId === r.id && expandedData && (
                <TableRow key={`exp-${r.id}`}>
                  <TableCell colSpan={13} sx={{ bgcolor: 'grey.50', p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={600}>Простої</Typography>
                        {expandedData.downtimes?.length === 0
                          ? <Typography variant="caption">Немає</Typography>
                          : expandedData.downtimes?.map((d) => (
                            <Box key={d.id} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip label={d.reason_code} size="small" color={d.downtime_type === 'loss' ? 'error' : 'default'} />
                              <Typography variant="body2">{d.reason_name}</Typography>
                              <Typography variant="body2" color="text.secondary">{d.minutes} хв</Typography>
                            </Box>
                          ))}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={600}>Брак</Typography>
                        {expandedData.defects?.length === 0
                          ? <Typography variant="caption">Немає</Typography>
                          : expandedData.defects?.map((d) => (
                            <Box key={d.id} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip label={d.reason_code} size="small" color="warning" />
                              <Typography variant="body2">{d.reason_name}</Typography>
                              <Typography variant="body2" color="text.secondary">{d.quantity} шт</Typography>
                            </Box>
                          ))}
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              ),
            ])}
            {!reports.length && !loading && (
              <TableRow><TableCell colSpan={13} align="center">
                <Alert severity="info" sx={{ border: 0 }}>За вибраний період звітів немає</Alert>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати звіт' : 'Новий звіт зміни'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Дата" type="date" value={form.date}
            onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
            InputLabelProps={{ shrink: true }} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Зміна</InputLabel>
            <Select value={form.work_shift} label="Зміна" onChange={(e) => {
              const ws = workShifts.find(s => s.id === e.target.value);
              let dur = form.shift_duration_min;
              if (ws && ws.start_time && ws.end_time) {
                const [sh, sm] = ws.start_time.split(':').map(Number);
                const [eh, em] = ws.end_time.split(':').map(Number);
                let mins = (eh * 60 + em) - (sh * 60 + sm);
                if (mins <= 0) mins += 1440;
                dur = mins;
              }
              setForm(f => ({ ...f, work_shift: e.target.value, shift_duration_min: dur }));
            }}>
              {workShifts.map(s => <MenuItem key={s.id} value={s.id}>{s.name}{s.start_time ? ` (${s.start_time}–${s.end_time})` : ''}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>ТПА</InputLabel>
            <Select value={form.machine} label="ТПА" onChange={(e) => setForm(f => ({ ...f, machine: e.target.value }))}>
              {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.identifier} {m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Норматив виробу</InputLabel>
            <Select value={form.product_norm} label="Норматив виробу"
              onChange={(e) => setForm(f => ({ ...f, product_norm: e.target.value }))}>
              {norms.map(n => <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Оператор" value={form.operator_name}
            onChange={(e) => setForm(f => ({ ...f, operator_name: e.target.value }))} fullWidth />
          <TextField label="Тривалість зміни, хв" type="number" value={form.shift_duration_min}
            onChange={(e) => setForm(f => ({ ...f, shift_duration_min: Number(e.target.value) }))} fullWidth />
          <TextField label="Вироблено, шт" type="number" value={form.total_produced}
            onChange={(e) => setForm(f => ({ ...f, total_produced: Number(e.target.value) }))} fullWidth />
          <TextField label="Фактичний цикл, с" type="number" value={form.actual_cycle_sec}
            onChange={(e) => setForm(f => ({ ...f, actual_cycle_sec: e.target.value }))} fullWidth />
          <TextField label="Коментар" value={form.comment} multiline rows={2}
            onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={save}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ──────────────────── Downtime/Defect Events tab ────────────────────
function EventsTab({ type }) {
  // type: 'downtimes' | 'defects'
  const [items, setItems] = useState([]);
  const [reports, setReports] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isDowntime = type === 'downtimes';
  const [form, setForm] = useState({ report: '', reason: '', minutes: 0, quantity: 0, comment: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/oee/${type}/?page_size=500`);
      setItems(r.data?.results || r.data || []);
    } finally { setLoading(false); }
  }, [type]);

  useEffect(() => {
    load();
    api.get('/oee/reports/?page_size=500').then(r => setReports(r.data?.results || r.data || []));
    const url = isDowntime ? '/oee/downtime-reasons/' : '/oee/defect-reasons/';
    api.get(url).then(r => setReasons(r.data?.results || r.data || []));
  }, [load, isDowntime]);

  const openNew = () => {
    setEditing(null);
    setForm({ report: '', reason: '', minutes: 0, quantity: 0, comment: '' });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({ report: row.report, reason: row.reason, minutes: row.minutes || 0, quantity: row.quantity || 0, comment: row.comment });
    setOpen(true);
  };

  const save = async () => {
    const payload = isDowntime
      ? { report: form.report, reason: form.reason, minutes: form.minutes, comment: form.comment }
      : { report: form.report, reason: form.reason, quantity: form.quantity, comment: form.comment };
    if (editing) await api.patch(`/oee/${type}/${editing}/`, payload);
    else await api.post(`/oee/${type}/`, payload);
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm('Видалити запис?')) return;
    await api.delete(`/oee/${type}/${id}/`); load();
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Додати</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Звіт зміни</TableCell>
              <TableCell>Код</TableCell>
              <TableCell>Причина</TableCell>
              {isDowntime && <><TableCell align="center">Тип</TableCell><TableCell align="center">Хвилин</TableCell></>}
              {!isDowntime && <TableCell align="center">Шт</TableCell>}
              <TableCell>Коментар</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {reports.find(r => r.id === item.report)
                    ? `${reports.find(r => r.id === item.report).date} ${reports.find(r => r.id === item.report).shift_display}`
                    : item.report}
                </TableCell>
                <TableCell><Chip label={item.reason_code} size="small" color={isDowntime ? (item.downtime_type === 'loss' ? 'error' : 'default') : 'warning'} /></TableCell>
                <TableCell>{item.reason_name}</TableCell>
                {isDowntime && <><TableCell align="center">
                  <Chip label={item.downtime_type === 'planned' ? 'Плановий' : 'Втрата'} size="small"
                    color={item.downtime_type === 'planned' ? 'default' : 'error'} />
                </TableCell><TableCell align="center">{item.minutes}</TableCell></>}
                {!isDowntime && <TableCell align="center">{item.quantity}</TableCell>}
                <TableCell>{item.comment || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => del(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && !loading && (
              <TableRow><TableCell colSpan={7} align="center">
                <Alert severity="info" sx={{ border: 0 }}>Записів немає</Alert>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати' : 'Додати запис'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Звіт зміни</InputLabel>
            <Select value={form.report} label="Звіт зміни" onChange={(e) => setForm(f => ({ ...f, report: e.target.value }))}>
              {reports.map(r => <MenuItem key={r.id} value={r.id}>{r.date} {r.shift_display} — {r.machine_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Причина</InputLabel>
            <Select value={form.reason} label="Причина" onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}>
              {reasons.map(r => <MenuItem key={r.id} value={r.id}>{r.code} — {r.name}</MenuItem>)}
            </Select>
          </FormControl>
          {isDowntime && <TextField label="Хвилин" type="number" value={form.minutes}
            onChange={(e) => setForm(f => ({ ...f, minutes: Number(e.target.value) }))} fullWidth />}
          {!isDowntime && <TextField label="Кількість браку, шт" type="number" value={form.quantity}
            onChange={(e) => setForm(f => ({ ...f, quantity: Number(e.target.value) }))} fullWidth />}
          <TextField label="Коментар" value={form.comment} multiline rows={2}
            onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={save}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ──────────────────── Plan tab ────────────────────
function PlanTab({ dateFrom, dateTo }) {
  const [plans, setPlans] = useState([]);
  const [reports, setReports] = useState([]);
  const [machines, setMachines] = useState([]);
  const [norms, setNorms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), shift: '', machine: '', product_norm: '', planned_qty: 0, deviation_reason: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const [planR, repR] = await Promise.all([
        api.get(`/oee/plans/?${p}`),
        api.get(`/oee/reports/?${p}`),
      ]);
      setPlans(planR.data?.results || planR.data || []);
      setReports(repR.data?.results || repR.data || []);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
    api.get('/machines/?page_size=200').then(r => setMachines(r.data?.results || r.data || []));
    api.get('/oee/product-norms/?page_size=500').then(r => setNorms(r.data?.results || r.data || []));
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ date: new Date().toISOString().slice(0, 10), shift: '', machine: '', product_norm: '', planned_qty: 0, deviation_reason: '' });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({ date: row.date, shift: row.shift || '', machine: row.machine, product_norm: row.product_norm, planned_qty: row.planned_qty, deviation_reason: row.deviation_reason });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, shift: form.shift || null };
    if (editing) await api.patch(`/oee/plans/${editing}/`, payload);
    else await api.post('/oee/plans/', payload);
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm('Видалити план?')) return;
    await api.delete(`/oee/plans/${id}/`); load();
  };

  const getFactQty = (plan) => {
    const rep = reports.find(r => r.date === plan.date && r.machine === plan.machine &&
      (!plan.shift || r.shift === plan.shift));
    return rep ? rep.good_qty : null;
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Новий план</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Дата</TableCell>
              <TableCell>Зміна</TableCell>
              <TableCell>ТПА</TableCell>
              <TableCell>Виріб</TableCell>
              <TableCell align="center">План шт</TableCell>
              <TableCell align="center">Факт шт</TableCell>
              <TableCell align="center">Виконання</TableCell>
              <TableCell>Причина відхилення</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => {
              const fact = getFactQty(plan);
              const fulfillment = plan.planned_qty > 0 && fact != null ? fact / plan.planned_qty : null;
              const color = fulfillment == null ? '#9e9e9e' : fulfillment >= 0.95 ? '#2e7d32' : fulfillment >= 0.80 ? '#f57c00' : '#c62828';
              return (
                <TableRow key={plan.id} hover>
                  <TableCell>{plan.date}</TableCell>
                  <TableCell>{plan.shift_display || 'Весь день'}</TableCell>
                  <TableCell>{plan.machine_name}</TableCell>
                  <TableCell>{plan.product_name}</TableCell>
                  <TableCell align="center">{num(plan.planned_qty)}</TableCell>
                  <TableCell align="center">{fact != null ? num(fact) : '—'}</TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700} sx={{ color }}>{pct(fulfillment)}</Typography>
                  </TableCell>
                  <TableCell>{plan.deviation_reason || '—'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(plan)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => del(plan.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {!plans.length && !loading && (
              <TableRow><TableCell colSpan={9} align="center">
                <Alert severity="info" sx={{ border: 0 }}>Планів немає</Alert>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати план' : 'Новий план'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Дата" type="date" value={form.date}
            onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Зміна</InputLabel>
            <Select value={form.shift} label="Зміна" onChange={(e) => setForm(f => ({ ...f, shift: e.target.value }))}>
              <MenuItem value="">Весь день</MenuItem>
              <MenuItem value="day">Денна</MenuItem>
              <MenuItem value="night">Нічна</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>ТПА</InputLabel>
            <Select value={form.machine} label="ТПА" onChange={(e) => setForm(f => ({ ...f, machine: e.target.value }))}>
              {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.identifier} {m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Норматив виробу</InputLabel>
            <Select value={form.product_norm} label="Норматив виробу"
              onChange={(e) => setForm(f => ({ ...f, product_norm: e.target.value }))}>
              {norms.map(n => <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Плановий обсяг, шт" type="number" value={form.planned_qty}
            onChange={(e) => setForm(f => ({ ...f, planned_qty: Number(e.target.value) }))} fullWidth />
          <TextField label="Причина відхилення" value={form.deviation_reason} multiline rows={2}
            onChange={(e) => setForm(f => ({ ...f, deviation_reason: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={save}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ──────────────────── Reference books tab ────────────────────
function ReferenceTab() {
  const [subTab, setSubTab] = useState(0);
  const [dtReasons, setDtReasons] = useState([]);
  const [defReasons, setDefReasons] = useState([]);
  const [norms, setNorms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDt, setOpenDt] = useState(false);
  const [openDef, setOpenDef] = useState(false);
  const [openNorm, setOpenNorm] = useState(false);
  const [editingDt, setEditingDt] = useState(null);
  const [editingDef, setEditingDef] = useState(null);
  const [editingNorm, setEditingNorm] = useState(null);
  const [dtForm, setDtForm] = useState({ code: '', name: '', downtime_type: 'loss' });
  const [defForm, setDefForm] = useState({ code: '', name: '' });
  const [normForm, setNormForm] = useState({ product: '', name: '', press_form_name: '', cavities: 1, norm_cycle_sec: 10, unit_cost_grn: 0, norm_source_note: '' });
  const [products, setProducts] = useState([]);
  const [selectedProductConfigs, setSelectedProductConfigs] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dt, def, nrm, prods] = await Promise.all([
        api.get('/oee/downtime-reasons/'),
        api.get('/oee/defect-reasons/'),
        api.get('/oee/product-norms/?page_size=500'),
        api.get('/products/?page_size=500'),
      ]);
      setDtReasons(dt.data?.results || dt.data || []);
      setDefReasons(def.data?.results || def.data || []);
      setNorms(nrm.data?.results || nrm.data || []);
      setProducts(prods.data?.results || prods.data || []);
    } finally { setLoading(false); }
  }, []);

  const onSelectNormProduct = (productId) => {
    const prod = products.find(p => p.id === Number(productId));
    const configs = prod?.manufacturing_configs || [];
    setSelectedProductConfigs(configs);
    setNormForm(f => ({ ...f, product: productId, name: prod?.name || f.name }));
  };

  const onSelectNormConfig = (configId) => {
    const cfg = selectedProductConfigs.find(c => c.id === Number(configId));
    if (!cfg) return;
    const pfLabel = cfg.press_form_identifier ? `${cfg.press_form_identifier} — ${cfg.press_form_name}` : cfg.press_form_name || '';
    setNormForm(f => ({
      ...f,
      press_form_name: pfLabel,
      cavities: cfg.number_of_cavities || cfg.press_form_total_cavities || 1,
      norm_cycle_sec: cfg.production_time_seconds || f.norm_cycle_sec,
    }));
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveDt = async () => {
    if (editingDt) await api.patch(`/oee/downtime-reasons/${editingDt}/`, dtForm);
    else await api.post('/oee/downtime-reasons/', dtForm);
    setOpenDt(false); loadAll();
  };
  const saveDef = async () => {
    if (editingDef) await api.patch(`/oee/defect-reasons/${editingDef}/`, defForm);
    else await api.post('/oee/defect-reasons/', defForm);
    setOpenDef(false); loadAll();
  };
  const saveNorm = async () => {
    if (editingNorm) await api.patch(`/oee/product-norms/${editingNorm}/`, normForm);
    else await api.post('/oee/product-norms/', normForm);
    setOpenNorm(false); loadAll();
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Причини простоїв" />
        <Tab label="Причини браку" />
        <Tab label="Нормативи виробів" />
      </Tabs>

      {/* ─ Причини простоїв ─ */}
      {subTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditingDt(null); setDtForm({ code: '', name: '', downtime_type: 'loss' }); setOpenDt(true); }}>Додати</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>Код</TableCell><TableCell>Причина</TableCell><TableCell>Тип</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {dtReasons.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><Chip label={r.code} size="small" /></TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell><Chip label={r.downtime_type === 'planned' ? 'Плановий' : 'Втрата'} size="small" color={r.downtime_type === 'planned' ? 'default' : 'error'} /></TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setEditingDt(r.id); setDtForm({ code: r.code, name: r.name, downtime_type: r.downtime_type }); setOpenDt(true); }}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={openDt} onClose={() => setOpenDt(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Причина простою</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField label="Код (напр. П10)" value={dtForm.code} onChange={(e) => setDtForm(f => ({ ...f, code: e.target.value }))} fullWidth />
              <TextField label="Назва" value={dtForm.name} onChange={(e) => setDtForm(f => ({ ...f, name: e.target.value }))} fullWidth />
              <FormControl fullWidth>
                <InputLabel>Тип</InputLabel>
                <Select value={dtForm.downtime_type} label="Тип" onChange={(e) => setDtForm(f => ({ ...f, downtime_type: e.target.value }))}>
                  <MenuItem value="planned">Плановий</MenuItem>
                  <MenuItem value="loss">Втрата</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDt(false)}>Скасувати</Button>
              <Button variant="contained" onClick={saveDt}>Зберегти</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ─ Причини браку ─ */}
      {subTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditingDef(null); setDefForm({ code: '', name: '' }); setOpenDef(true); }}>Додати</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>Код</TableCell><TableCell>Причина</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {defReasons.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><Chip label={r.code} size="small" color="warning" /></TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setEditingDef(r.id); setDefForm({ code: r.code, name: r.name }); setOpenDef(true); }}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={openDef} onClose={() => setOpenDef(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Причина браку</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField label="Код (напр. Б01)" value={defForm.code} onChange={(e) => setDefForm(f => ({ ...f, code: e.target.value }))} fullWidth />
              <TextField label="Назва" value={defForm.name} onChange={(e) => setDefForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDef(false)}>Скасувати</Button>
              <Button variant="contained" onClick={saveDef}>Зберегти</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ─ Нормативи виробів ─ */}
      {subTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditingNorm(null); setSelectedProductConfigs([]); setNormForm({ product: '', name: '', press_form_name: '', cavities: 1, norm_cycle_sec: 10, unit_cost_grn: 0, norm_source_note: '' }); setOpenNorm(true); }}>Додати</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>Назва</TableCell><TableCell>Прес-форма</TableCell>
                  <TableCell align="center">Гнізд</TableCell><TableCell align="center">Цикл, с</TableCell>
                  <TableCell align="center">Такт, с</TableCell><TableCell align="center">Собівартість</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {norms.map(n => (
                  <TableRow key={n.id} hover>
                    <TableCell>{n.name}</TableCell>
                    <TableCell>{n.press_form_name || '—'}</TableCell>
                    <TableCell align="center">{n.cavities}</TableCell>
                    <TableCell align="center">{n.norm_cycle_sec}</TableCell>
                    <TableCell align="center">{n.takt_sec != null ? n.takt_sec.toFixed(2) : '—'}</TableCell>
                    <TableCell align="center">{n.unit_cost_grn} грн</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => {
                        setEditingNorm(n.id);
                        setSelectedProductConfigs([]); setNormForm({ product: n.product || '', name: n.name, press_form_name: n.press_form_name, cavities: n.cavities, norm_cycle_sec: n.norm_cycle_sec, unit_cost_grn: n.unit_cost_grn, norm_source_note: n.norm_source_note });
                        setOpenNorm(true);
                      }}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={openNorm} onClose={() => setOpenNorm(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Норматив виробу</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Виріб (з ЕРП)</InputLabel>
                <Select value={normForm.product || ''} label="Виріб (з ЕРП)"
                  onChange={(e) => onSelectNormProduct(e.target.value)}>
                  <MenuItem value="">— не обрано —</MenuItem>
                  {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
              {selectedProductConfigs.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Прес-форма + Станок</InputLabel>
                  <Select value="" label="Прес-форма + Станок" onChange={(e) => onSelectNormConfig(e.target.value)}>
                    <MenuItem value="">— оберіть для автозаповнення —</MenuItem>
                    {selectedProductConfigs.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.press_form_identifier} — {c.press_form_name} / {c.machine_identifier} ({c.production_time_seconds}с, {c.number_of_cavities || c.press_form_total_cavities} гн.)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField label="Назва виробу" value={normForm.name} onChange={(e) => setNormForm(f => ({ ...f, name: e.target.value }))} fullWidth />
              <TextField label="Прес-форма" value={normForm.press_form_name} onChange={(e) => setNormForm(f => ({ ...f, press_form_name: e.target.value }))} fullWidth />
              <TextField label="Кількість гнізд" type="number" value={normForm.cavities} onChange={(e) => setNormForm(f => ({ ...f, cavities: Number(e.target.value) }))} fullWidth />
              <TextField label="Нормативний цикл, с" type="number" value={normForm.norm_cycle_sec} onChange={(e) => setNormForm(f => ({ ...f, norm_cycle_sec: Number(e.target.value) }))} fullWidth />
              <TextField label="Собівартість, грн/шт" type="number" value={normForm.unit_cost_grn} onChange={(e) => setNormForm(f => ({ ...f, unit_cost_grn: e.target.value }))} fullWidth />
              <TextField label="Джерело нормативу" value={normForm.norm_source_note} onChange={(e) => setNormForm(f => ({ ...f, norm_source_note: e.target.value }))} fullWidth />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenNorm(false)}>Скасувати</Button>
              <Button variant="contained" onClick={saveNorm}>Зберегти</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
}

// ──────────────────── Main page ────────────────────
export default function OEEPage() {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(weekAgo);
  const [dateTo, setDateTo] = useState(today);
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>OEE — Загальна ефективність обладнання</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField label="Від" type="date" size="small" value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField label="До" type="date" size="small" value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Дашборд" />
        <Tab label="Звіти змін" />
        <Tab label="Простої" />
        <Tab label="Брак" />
        <Tab label="План" />
        <Tab label="Довідники" />
      </Tabs>

      {tab === 0 && <DashboardTab dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === 1 && <ReportsTab dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === 2 && <EventsTab type="downtimes" />}
      {tab === 3 && <EventsTab type="defects" />}
      {tab === 4 && <PlanTab dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === 5 && <ReferenceTab />}
    </Box>
  );
}
